import { ReactNode, useState, useCallback } from 'react';
import { AppBar, Box, Drawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Toolbar, Tooltip, Typography, Container, Stack, Chip, Menu, MenuItem, Divider } from '@mui/material';
import { UserAvatar } from './UserAvatar';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useThemeMode } from '../themeContext';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HomeIcon from '@mui/icons-material/Home';
import GavelIcon from '@mui/icons-material/Gavel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';

// Prefetch map: hovering a nav link preloads the page chunk so navigation feels instant.
const PREFETCH_MAP: Record<string, () => void> = {
  '/': () => import('../pages/Dashboard'),
  '/about': () => import('../pages/About'),
  '/workshops': () => import('../pages/Workshops'),
  '/agenda': () => import('../pages/Agenda'),
  '/execution': () => import('../pages/Execution'),
  '/history': () => import('../pages/History'),
  '/decisions': () => import('../pages/Decisions'),
  '/tasks': () => import('../pages/Tasks'),
  '/users': () => import('../pages/UsersAdmin'),
  '/audit': () => import('../pages/Audit'),
  '/settings': () => import('../pages/Settings'),
};

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

export function Layout({ children }: { children: ReactNode }) {
  const { state, hasRole } = useAuth();
  const { mode, toggle: toggleTheme } = useThemeMode();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (state.status !== 'authenticated') return <>{children}</>;

  // Nav is grouped into a few labelled sections so the rail reads as a short list
  // rather than a long undifferentiated stack.
  const navSections: Array<{ heading: string | null; items: Array<{ to: string; label: string; icon: ReactNode; show: boolean }> }> = [
    { heading: null, items: [
      { to: '/', label: 'Home', icon: <HomeIcon />, show: true },
    ] },
    { heading: 'Workshop', items: [
      { to: '/workshops', label: 'Workshops', icon: <CalendarMonthIcon />, show: hasRole('SuperAdmin', 'Admin') },
      { to: '/agenda',    label: 'Agenda',    icon: <EventNoteIcon />, show: true },
      { to: '/execution', label: 'Execution', icon: <PlayCircleOutlineIcon />, show: true },
    ] },
    { heading: 'Records', items: [
      { to: '/history',   label: 'History',   icon: <HistoryIcon />, show: true },
      { to: '/decisions', label: 'Decisions', icon: <GavelIcon />, show: true },
      { to: '/tasks',     label: 'Tasks',     icon: <AssignmentIcon />, show: true },
    ] },
    { heading: 'Administration', items: [
      { to: '/users',    label: 'Users',    icon: <PeopleIcon />, show: hasRole('SuperAdmin', 'Admin') },
      { to: '/audit',    label: 'Audit',    icon: <FactCheckIcon />, show: hasRole('SuperAdmin', 'Admin') },
      { to: '/settings', label: 'Settings', icon: <SettingsIcon />, show: hasRole('SuperAdmin') },
    ] },
    { heading: null, items: [
      { to: '/about', label: 'About', icon: <InfoOutlinedIcon />, show: true },
    ] },
  ];

  const prefetch = useCallback((path: string) => {
    PREFETCH_MAP[path]?.();
  }, []);

  const width = open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED;

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: t => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <Tooltip title={open ? 'Collapse menu' : 'Expand menu'}>
            <IconButton onClick={() => setOpen(o => !o)} edge="start" sx={{ mr: 1.5 }}>
              {open ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Tooltip>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ flexGrow: 1 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '10px',
              background: 'linear-gradient(135deg, #4ea8ff 0%, #7e5cd9 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', boxShadow: '0 4px 12px rgba(78,168,255,0.35)',
            }}>
              <EventNoteOutlinedIcon fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 700, letterSpacing: '-0.01em' }}>
                Workshop Planner
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
                Softura
              </Typography>
            </Box>
          </Stack>
          <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton onClick={toggleTheme} sx={{ mr: 0.5 }}>
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <IconButton onClick={e => setAnchorEl(e.currentTarget)} sx={{ p: 0.5, ml: 0.5 }}>
              <UserAvatar userId={state.user.id} displayName={state.user.displayName} size={38} />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, minWidth: 264, borderRadius: 2, boxShadow: 6 } }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UserAvatar userId={state.user.id} displayName={state.user.displayName} size={46} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>{state.user.displayName}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{state.user.email}</Typography>
                <Chip label={state.user.role} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', mt: 0.5 }} />
              </Box>
            </Box>
            <Divider />
            <MenuItem component="a" href="/logout" sx={{ py: 1.2 }}>
              <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>
              Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width, boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: t => t.transitions.create('width', { duration: t.transitions.duration.short }),
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', mt: 1.5 }}>
          <List sx={{ px: 1 }}>
            {navSections.map((section, si) => {
              const items = section.items.filter(i => i.show);
              if (items.length === 0) return null;
              const showDivider = (!open && si > 0) || (open && !section.heading && si > 0);
              return (
                <Box key={si}>
                  {showDivider && <Divider sx={{ mx: 1, my: 1 }} />}
                  {open && section.heading && (
                    <ListSubheader
                      disableSticky
                      sx={{
                        bgcolor: 'transparent', lineHeight: '28px', mt: si === 0 ? 0 : 1.5,
                        fontSize: '0.68rem', letterSpacing: '0.09em', textTransform: 'uppercase',
                        fontWeight: 700, color: 'text.disabled',
                      }}
                    >
                      {section.heading}
                    </ListSubheader>
                  )}
                  {items.map(i => (
                    <Tooltip key={i.to} title={open ? '' : i.label} placement="right">
                      <ListItemButton
                        selected={i.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(i.to)}
                        onClick={() => nav(i.to)}
                        onMouseEnter={() => prefetch(i.to)}
                        sx={{ py: 1, mb: 0.25, borderRadius: 1.5, justifyContent: open ? 'flex-start' : 'center' }}
                      >
                        <ListItemIcon sx={{ minWidth: open ? 44 : 0, justifyContent: 'center' }}>{i.icon}</ListItemIcon>
                        {open && <ListItemText primary={i.label} primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />}
                      </ListItemButton>
                    </Tooltip>
                  ))}
                </Box>
              );
            })}
          </List>
        </Box>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 4, mt: 8, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 64px)' }}>
        <Container maxWidth="xl" disableGutters sx={{ flex: 1 }}>
          {children}
        </Container>
        <Box sx={{
          mt: 3, pt: 1.5, pb: 0.5, borderTop: '1px solid', borderColor: 'divider',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
            Softura Confidential
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
            Workshop Planner · v{__APP_VERSION__}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
